import type { Feature } from './logging/logFeature';
import type { AnyAction } from 'redux';
import type { ThunkDispatch as ReduxThunkDispatch } from 'redux-thunk';

export interface Action<P> {
    type: string;
    payload: P;
    error?: boolean;
    getLogProperties?: (action: Action<P>) => {};
}

function withLogger<P>(
    internalCreateAction: (payload: P) => Action<P>,
    getLogProperties: (action: Action<P>) => {},
    type: string
): (payload: P) => Action<P> {
    const factory = (payload: P) => {
        const action = internalCreateAction(payload);

        return {
            ...action,
            getLogProperties: getLogProperties,
        } as Action<P>;
    };

    setActionName(factory, type);
    return factory;
}

function defaultGetProperties(): {} {
    return null;
}

const defaultErrorProperties = { isError: true };
const withDefaultErrorProperties = <ErrorPayload>(
    getErrorProp: (action: Action<ErrorPayload>) => {}
) => (action: Action<ErrorPayload>) => {
    if (getErrorProp) {
        return { ...getErrorProp(action), ...defaultErrorProperties };
    }
    return { ...defaultErrorProperties };
};

/**
 * Creates an action factory which is responsible for creating actions with the specified type and payload
 * @param name Type of the action. It should be unique across the whole application
 * @param getLogProperties Function which returns action's properties which should be logged together with action's type.
 *        The action's type is logged by default.
 *        Provide null if you want to skip logging for that action.
 * @param getErrorProperties Function which returns additional properties which should be logged when this action
 *        is error action.
 */
export const createActionFactory = function <Payload = void>(
    name: string,
    feature: Feature,
    getLogProperties: (action: Action<Payload>) => {} = defaultGetProperties
): (payload?: Payload) => Action<Payload> {
    name = getName(name, feature);
    return withLogger<Payload>(actionFactory<Payload>(name), getLogProperties, name);
};

export const createActionFactoryForFeature = (feature: Feature) => <Payload>(
    name: string,
    getLogProperties = defaultGetProperties
) => createActionFactory<Payload>(name, feature, getLogProperties);

export interface DataActionFactory<SuccessPayload, ErrorPayload> {
    success: (payload?: SuccessPayload) => Action<SuccessPayload>;
    error: (errorPayload?: ErrorPayload) => Action<ErrorPayload>;
}

function actionFactory<P>(name: string, error: boolean = false): (payload: P) => Action<P> {
    const factory = (payload: P) => ({
        type: name,
        error,
        payload,
    });

    setActionName(factory, name);
    return factory;
}

/**
 * Creates DataAction factory.
 * @param name - Name of the action
 * @param getSuccessLogProperties - method to obtain additional logging properties for success action
 * @param getErrorLogProperties - method to obtain additional logging properties for error action
 */
export function createDataActionFactory<Payload, ErrorPayload>(
    name: string,
    feature: Feature,
    getSuccessLogProperties: (action: Action<Payload>) => {} = defaultGetProperties,
    getErrorLogProperties: (action: Action<ErrorPayload>) => {} = null
): DataActionFactory<Payload, ErrorPayload> {
    name = getName(name, feature);
    return {
        success: withLogger<Payload>(actionFactory<Payload>(name), getSuccessLogProperties, name),
        error: withLogger<ErrorPayload>(
            actionFactory<ErrorPayload>(name, true),
            withDefaultErrorProperties(getErrorLogProperties),
            name
        ),
    };
}

const getName = (name: string, feature: Feature) => `${feature}.${name}`;

export interface ThunkDispatch extends ReduxThunkDispatch<{}, {}, AnyAction> {}

export const createThunk = <T>(name: string, thunk: T) => {
    setActionName(thunk, name);
    return thunk;
};
export const setActionName = (action: any, name: string) => {
    action.toString = () => name;
    action.type = name;
};
